import React, { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Types
export type ModalType = 'info' | 'success' | 'error' | 'warning';

export type ModalAction = {
  label: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
};

export type ModalPayload = {
  id?: string;
  type?: ModalType;
  title?: string;
  message?: string;
  actions?: ModalAction[];
  autoClose?: number | null; // ms or null to disable
};

type GlobalModalContextValue = {
  show: (payload: ModalPayload) => string; // returns id
  hide: (id?: string) => void;
};

const GlobalModalContext = createContext<GlobalModalContextValue | null>(null);

export function useGlobalModal() {
  const ctx = useContext(GlobalModalContext);
  if (!ctx) throw new Error('useGlobalModal must be used within GlobalModalProvider');
  return ctx;
}

// Small helper to generate id
function genId() {
  return Math.random().toString(36).slice(2, 9);
}

export default function GlobalModalProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<ModalPayload[]>([]);
  const [current, setCurrent] = useState<ModalPayload | null>(null);
  const timeoutRef = useRef<number | null>(null);

  // show - pushes into queue
  const show = useCallback((payload: ModalPayload) => {
    const id = payload.id ?? genId();
    const entry = { ...payload, id };
    setQueue(q => [...q, entry]);
    // if nothing showing, will pick it up in effect
    return id;
  }, []);

  const hide = useCallback((id?: string) => {
    // if id matches current, close it; otherwise remove from queue
    if (id && current?.id === id) {
      setCurrent(null);
    } else if (!id) {
      setCurrent(null);
    } else {
      setQueue(q => q.filter(x => x.id !== id));
    }
  }, [current]);

  // when current is null and queue not empty, pop next
  useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(q => q.slice(1));
    }
  }, [queue, current]);

  // autoClose handling
  useEffect(() => {
    if (!current) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }
    const ms = current.autoClose ?? 4000; // default 4s
    if (ms && ms > 0) {
      timeoutRef.current = window.setTimeout(() => {
        setCurrent(null);
      }, ms);
    }
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [current]);

    // 1) dentro do GlobalModalProvider (adicione este useEffect para bloquear scroll do body)
  useEffect(() => {
    if (!current) return;
    const prev = document.body.style.overflow;
    // bloqueia scroll do body ao abrir modal
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [current]);


  const value = {
    show,
    hide,
  };

  return (
    <GlobalModalContext.Provider value={value}>
      {children}
      <ModalHost current={current} onClose={() => setCurrent(null)} />
    </GlobalModalContext.Provider>
  );
}

function iconFor(type: ModalType | undefined) {
  switch (type) {
    case 'success':
      return (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M12 9v4" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    default:
      return (
        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01" />
        </svg>
      );
  }
}

function ModalHost({ current, onClose }: { current: ModalPayload | null; onClose: () => void }) {
  if (!current) return null;

  const type = current.type ?? "info";
  const iconColorClassMap: Record<ModalType, string> = {
    success: "text-emerald-300",
    error: "text-rose-300",
    warning: "text-amber-300",
    info: "text-sky-300",
  };
  const iconBgClassMap: Record<ModalType, string> = {
    success: "bg-emerald-500/15",
    error: "bg-rose-500/15",
    warning: "bg-amber-500/15",
    info: "bg-sky-500/15",
  };

  const iconColorClass = iconColorClassMap[type];
  const iconBgClass = iconBgClassMap[type];
  const fallbackTitle =
    type === "error"
      ? "Não foi possível concluir"
      : type === "success"
      ? "Tudo certo"
      : type === "warning"
      ? "Atenção"
      : "Informação";

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          // NOTE: items-center em vez de items-end — centraliza em mobile e desktop
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ zIndex: 11000, background: "rgba(5, 2, 12, 0.56)" }}
          aria-hidden={false}
        >
          <motion.div
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 24, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="pointer-events-auto relative w-full max-w-2xl sm:mx-4 rounded-2xl overflow-hidden border border-[#4a2a74] bg-[#140b24] text-white shadow-[0_24px_60px_rgba(73,15,112,0.45)]"
            role="alertdialog"
            aria-live="assertive"
            aria-modal="true"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#490f70] via-[#791cb8] to-[#cb88f9]" />
            <div className="flex p-5 gap-4 items-start">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border border-white/10 ${iconBgClass} ${iconColorClass}`}
                aria-hidden
              >
                {iconFor(type)}
              </div>

              <div className="flex-1">
                <div className="font-semibold text-lg leading-tight mb-1 text-white">
                  {current.title ?? fallbackTitle}
                </div>
                <div className="text-sm text-[#e8def7] whitespace-pre-line">
                  {current.message}
                </div>

                {current.actions && current.actions.length > 0 && (
                  <div className="mt-4 flex gap-2">
                    {current.actions.map((a, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          try { a.onClick?.(); } catch (e) { /* ignore */ }
                          onClose();
                        }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                          a.variant === "danger"
                            ? "border-rose-400/40 text-rose-100 bg-rose-500/20"
                            : a.variant === "primary"
                            ? "bg-[#791cb8] text-white border-[#a85add] hover:bg-[#8b2fc8]"
                            : "bg-white/10 text-white border-white/20 hover:bg-white/20"
                        }`}
                      >
                        {a.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex-shrink-0">
                <button
                  onClick={onClose}
                  aria-label="Fechar"
                  className="p-2 rounded-md text-white/70 hover:bg-white/10 hover:text-white"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
